import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { ExpandMore, Psychology } from '@mui/icons-material';

interface PIProfile {
  id: number;
  name: string;
  code: string;
  description: string;
  characteristics: string;
  strengths: string;
  cautions: string;
  behavioral_drives: string;
  cognitive_drives: string;
}

interface BehavioralDrives {
  dominance: string;
  extraversion: string;
  patience: string;
  formality: string;
}

interface CognitiveDrives {
  analytical: string;
  conceptual: string;
  practical: string;
}

const PIProfiles: React.FC = () => {
  const [profiles, setProfiles] = useState<PIProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/pi-profiles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch PI profiles');
      }
      
      const data = await response.json();
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Failed to load PI profiles:', error);
      setError('Failed to load PI profiles');
    } finally {
      setLoading(false);
    }
  };

  const getDriveColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const parseDrives = (drivesJson: string) => {
    try {
      return JSON.parse(drivesJson);
    } catch {
      return {};
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          PI Profiles
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Psychology sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">
          Predictive Index Profiles
        </Typography>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          The Predictive Index identifies 17 distinct workplace personality profiles. Each profile represents 
          a unique combination of behavioral and cognitive drives that influence how individuals approach work, 
          make decisions, and interact with others.
        </Typography>
      </Paper>

      {profiles.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Psychology sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No PI profiles available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            PI profile data needs to be seeded in the database
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3 }}>
          {profiles.map((profile) => {
            const behavioralDrives: BehavioralDrives = parseDrives(profile.behavioral_drives);
            const cognitiveDrives: CognitiveDrives = parseDrives(profile.cognitive_drives);
            
            return (
              <Card key={profile.id} sx={{ height: 'fit-content' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {profile.name}
                    </Typography>
                    <Chip 
                      label={profile.code} 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    {profile.description}
                  </Typography>

                  <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Detailed Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Key Characteristics
                          </Typography>
                          <Typography variant="body2">
                            {profile.characteristics}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="success.main" gutterBottom>
                            Strengths
                          </Typography>
                          <Typography variant="body2">
                            {profile.strengths}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="warning.main" gutterBottom>
                            Cautions
                          </Typography>
                          <Typography variant="body2">
                            {profile.cautions}
                          </Typography>
                        </Box>

                        <Divider />

                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Behavioral Drives
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {Object.entries(behavioralDrives).map(([drive, level]) => (
                              <Chip
                                key={drive}
                                label={`${drive.charAt(0).toUpperCase() + drive.slice(1)}: ${level}`}
                                size="small"
                                color={getDriveColor(level) as any}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Cognitive Drives
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {Object.entries(cognitiveDrives).map(([drive, level]) => (
                              <Chip
                                key={drive}
                                label={`${drive.charAt(0).toUpperCase() + drive.slice(1)}: ${level}`}
                                size="small"
                                color={getDriveColor(level) as any}
                                variant="filled"
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default PIProfiles;